import { useState } from 'react';

import { Close, Save } from '@mui/icons-material';
import { Box, IconButton, MenuItem, TableCell, TableRow, TextField } from '@mui/material';

import { PrimitiveType, SimplePropertyModel } from '../../../api/api';
import { getRequiredFieldProps, usePropertySaveValidation } from './usePropertySaveValidation';

interface Props {
	onSave: (property: SimplePropertyModel) => Promise<void> | void;
	onIsEditingChanged: (isEditing: boolean) => void;
	propertyTypes: PrimitiveType[];
	name: string;
	onNameChange: (value: string) => void;
	description: string;
	onDescriptionChange: (value: string) => void;
	isRequired: boolean;
	onIsRequiredChange: (value: boolean) => void;
	isMultiple: boolean;
	onIsMultipleChange: (value: boolean) => void;
}

const AddSimplePropertyRow: React.FC<Props> = ({
	onSave,
	onIsEditingChanged,
	propertyTypes,
	name,
	onNameChange,
	description,
	onDescriptionChange,
	isRequired,
	onIsRequiredChange,
	isMultiple,
	onIsMultipleChange,
}) => {
	const [propertyType, setPropertyType] = useState<PrimitiveType>(PrimitiveType.String);
	const [defaultValue, setDefaultValue] = useState('');

	const isNameValid = name.trim().length > 0;
	const isTypeValid = !!propertyType;
	const canSave = isNameValid && isTypeValid;
	const { saveAttempted, validateForSave, resetValidation } = usePropertySaveValidation(canSave);

	const handleSave = async () => {
		if (!validateForSave()) {
			return;
		}

		try {
			await onSave({ name, description, isRequired, isMultiple, propertyType, defaultValue });
			setPropertyType(PrimitiveType.String);
			setDefaultValue('');
			resetValidation();
			onIsEditingChanged(false);
		} catch (err) {
			console.error('Failed to save property', err);
		}
	};

	return (
		<>
			<TableRow>
				<TableCell>
					<TextField
						variant="outlined"
						size="small"
						fullWidth
						{...getRequiredFieldProps(saveAttempted && !isNameValid)}
						value={name}
						onChange={(e) => onNameChange(e.target.value)}
					/>
				</TableCell>
				<TableCell>
					<>
						<TextField
							select
							variant="outlined"
							size="small"
							fullWidth
							{...getRequiredFieldProps(saveAttempted && !isTypeValid)}
							value={propertyType}
							onChange={(e) => setPropertyType(e.target.value as PrimitiveType)}
						>
							{propertyTypes.map((option) => (
								<MenuItem key={option} value={option}>
									{option}
								</MenuItem>
							))}
						</TextField>
					</>
				</TableCell>
				<TableCell>
					<Box display="flex" justifyContent="left">
						<input type="checkbox" checked={isRequired} onChange={(e) => onIsRequiredChange(e.target.checked)} />
					</Box>
				</TableCell>
				<TableCell>
					<Box display="flex" justifyContent="left">
						<input type="checkbox" checked={isMultiple} onChange={(e) => onIsMultipleChange(e.target.checked)} />
					</Box>
				</TableCell>
				<TableCell colSpan={1}>
					<Box display="flex" alignItems="center">
						<TextField variant="outlined" size="small" fullWidth value={defaultValue} onChange={(e) => setDefaultValue(e.target.value)} />
					</Box>
				</TableCell>
				<TableCell>
					<Box display="flex" justifyContent="right">
						<IconButton onClick={handleSave} size="small" color="primary">
							<Save />
						</IconButton>
						<IconButton onClick={() => onIsEditingChanged(false)} size="small">
							<Close />
						</IconButton>
					</Box>
				</TableCell>
			</TableRow>
			<TableRow>
				<TableCell colSpan={6}>
					<TextField
						label="Description"
						variant="outlined"
						size="small"
						fullWidth
						multiline
						value={description}
						onChange={(e) => onDescriptionChange(e.target.value)}
					/>
				</TableCell>
			</TableRow>
		</>
	);
};

export default AddSimplePropertyRow;
